import type {
  DatasetManifestEntry,
  Measure,
  ReportDefinition,
} from '../types/reporting';
import type { ResultRow } from './arrowToRows';
import { NO_RELATED, type RelatedManifests } from './columnRefs';
import {
  CHART_CATEGORY_TOTAL_COLUMN,
  type DetailChartCompileDefinition,
  chartConfigValid,
} from './compileReport';

export { CHART_CATEGORY_TOTAL_COLUMN };

/**
 * Whether this DETAIL report can render a chart: it has no group-bys of its own
 * (a grouped report charts through its own group_bys/measures instead) and it
 * carries a usable authored `chart` block.
 *
 * The predicate is SHARED with `validateDefinition` (both call
 * `chartConfigValid`), so the Chart tab and the stored definition can never
 * disagree — a config validateDefinition would drop can never gate a tab open.
 */
export function validChartConfig(
  definition: ReportDefinition,
  dataset: DatasetManifestEntry,
  related: RelatedManifests = NO_RELATED
): boolean {
  if (definition.group_bys.length > 0) return false;
  return chartConfigValid(definition, dataset, related);
}

/**
 * The synthetic GROUPED definition that backs a detail report's Chart tab.
 *
 * Presentation-derived and never persisted: `group_bys`/`measures` on the stored
 * definition are left alone, so the Table tab's claim-grain contract is intact.
 *
 * FILTERS ARE COPIED VERBATIM so `deriveExtractScope` resolves the identical
 * extract scope and the chart query hits the already-registered extract instead
 * of minting a second one.
 *
 * `gap_fill` is STRIPPED: it is meaningless at chart grain, and a saved or
 * API-authored definition can legitimately carry both fields, so excluding date
 * dimensions in the UI would not be sufficient.
 *
 * `sort: []` IS MANDATORY, not cosmetic. The detail sort addresses detail
 * columns and has no meaning at chart grain, and — the concrete failure this
 * prevents — a detail sort naming the chart's own group_by (the user clicked the
 * Payer column header on the Table tab, writing `sort: [{by: 'payer_name'}]`)
 * would otherwise flow into `orderClause` in the compiler's grouped branch and
 * hijack the top-N ordering, selecting the alphabetically-first 50 payers
 * instead of the largest 50 while the caption still claims a top-N. The compiler
 * additionally emits its measure-DESC ordering unconditionally for a
 * chart-marked compile, so the two defenses are independent.
 *
 * `pivot_column: null` IS ALSO MANDATORY, for the same reason as `sort: []`. A
 * chart has no pivot at chart grain, and — the concrete failure this prevents —
 * a detail definition that still carries a stale `pivot_column` (e.g. the user
 * added a group-by, turned on "Break out into columns", then removed the
 * group-by and the measure, which neither `removeGroupBy` nor the FieldPanel UI
 * clears) would otherwise spread verbatim into this derived definition. Because
 * `pivot_column && measures.length > 0` is checked BEFORE the grouped/detail-
 * chart branch in compileReport's branch selection, an un-neutralized pivot
 * would win over the `__detail_chart` marker: the emitted SQL would have no
 * `LIMIT MAX_CATEGORIES` and no category-total column, breaking the guarantee
 * that no dimension can materialize an unbounded result.
 */
export function detailChartDefinition(
  definition: ReportDefinition
): ReportDefinition {
  const chart = definition.chart;
  if (!chart) {
    throw new Error('detailChartDefinition requires definition.chart');
  }
  const measure: Measure =
    chart.agg === 'count'
      ? { agg: 'count' }
      : { agg: 'sum', column: chart.column };
  const derived: DetailChartCompileDefinition = {
    ...definition,
    group_bys: [{ column: chart.group_by }],
    measures: [measure],
    view: 'bar',
    sort: [],
    pivot_column: null,
    __detail_chart: true,
  };
  delete derived.gap_fill;
  return derived;
}

/**
 * Split the category-total scalar out of a chart run's result.
 *
 * `ResultsChart` treats every non-dimension column as a chartable series, so the
 * total must never reach it — it would be drawn as a bar. Returns the total so
 * the UI can disclose "showing top N of M categories".
 */
export function splitDetailChartResult(
  columns: string[],
  rows: ResultRow[]
): { columns: string[]; rows: ResultRow[]; categoryTotal: number | null } {
  if (!columns.includes(CHART_CATEGORY_TOTAL_COLUMN)) {
    return { columns, rows, categoryTotal: null };
  }
  const raw = rows[0]?.[CHART_CATEGORY_TOTAL_COLUMN];
  const parsed =
    raw === null || raw === undefined || typeof raw === 'boolean'
      ? Number.NaN
      : Number(raw);
  return {
    columns: columns.filter(c => c !== CHART_CATEGORY_TOTAL_COLUMN),
    rows: rows.map(row => {
      const next = { ...row };
      delete next[CHART_CATEGORY_TOTAL_COLUMN];
      return next;
    }),
    categoryTotal: Number.isFinite(parsed) ? parsed : null,
  };
}
